import { ChangeEvent, FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, formatDate, formatPrice, Order, Service, User } from '../api';
import { useAuth } from '../auth/AuthContext';
import Footer from '../Components/Footer';
import { Alert, Loading, StatusTag, useAction, useLoad } from '../Components/ui';

const typeLabel: Record<Service['type'], string> = {
    wordpress: 'WordPress hosting',
    static: 'Static hosting',
    domain: 'Domain',
    dns: 'DNS hosting',
    database: 'Database',
};

const TYPE_ORDER: Record<Service['type'], number> = { wordpress: 0, static: 1, dns: 2, database: 3, domain: 4 };
const LIVE: Service['status'][] = ['provisioning', 'active', 'suspended'];
const isLive = (s: Service) => LIVE.includes(s.status);

interface DomainGroup {
    /** Lower-cased domain name, or '' for services without one. */
    domain: string;
    /** The customer's domain service (registration or transfer), or null for a domain registered elsewhere. */
    owned: Service | null;
    packages: Service[];
}

/**
 * The domain is the master entity: every service is filed under its domain name, and the domain's own
 * service (if it's registered with us) heads the group. Packages linked to a domain service go under
 * that domain even if the names would differ.
 */
function groupByDomain(services: Service[]): DomainGroup[] {
    const groups = new Map<string, DomainGroup>();
    const byId = new Map(services.map(s => [s.id, s]));
    const keyOf = (s: Service) => (byId.get(s.linkedDomainServiceId ?? -1)?.domain ?? s.domain ?? '').toLowerCase();
    const group = (key: string) => {
        if (!groups.has(key)) groups.set(key, { domain: key, owned: null, packages: [] });
        return groups.get(key)!;
    };
    // A live domain service wins the header over an old terminated or failed one.
    const domains = services.filter(s => s.type === 'domain').sort((a, b) => Number(isLive(b)) - Number(isLive(a)));
    for (const d of domains) {
        const g = group(keyOf(d));
        if (!g.owned) g.owned = d;
        else g.packages.push(d);
    }
    for (const s of services.filter(x => x.type !== 'domain')) group(keyOf(s)).packages.push(s);
    for (const g of groups.values()) {
        // Live first; web packages before the extra databases mounted on them.
        g.packages.sort((a, b) => Number(isLive(b)) - Number(isLive(a)) || TYPE_ORDER[a.type] - TYPE_ORDER[b.type] || b.id - a.id);
    }
    // Live domains first, then by name; services without a domain last.
    return [...groups.values()].sort((a, b) =>
        Number(!a.domain) - Number(!b.domain)
        || Number(isLive(b.owned ?? b.packages[0])) - Number(isLive(a.owned ?? a.packages[0]))
        || a.domain.localeCompare(b.domain));
}

function DomainGroupCard({ group }: { group: DomainGroup }) {
    const { owned, packages } = group;
    const hasHosting = packages.some(p => (p.type === 'wordpress' || p.type === 'static') && isLive(p));
    const header = (
        <>
            <div className="domain-group-title">
                <span className="service-domain">{group.domain || 'Without a domain'}</span>
                {owned ? <StatusTag status={owned.status} /> : group.domain && <span className="tag tag-muted">External domain</span>}
            </div>
            <span className="meta">
                {owned
                    ? `${owned.productName} · renews ${formatDate(owned.expiresAt)}`
                    : group.domain ? 'Registered elsewhere — point it to us from the package below' : 'Services not tied to a domain name'}
            </span>
        </>
    );
    return (
        <div className="domain-group">
            {owned
                ? <Link to={`/panel/${owned.id}`} className="domain-group-head">{header}</Link>
                : <div className="domain-group-head">{header}</div>}
            {packages.length > 0 && (
                <ul className="domain-packages">
                    {packages.map(p => (
                        <li key={p.id}>
                            <Link to={`/panel/${p.id}`} className="domain-package">
                                <span className="tag tag-category">{typeLabel[p.type]}</span>
                                <span className="domain-package-name">{p.productName}{p.mountedDatabase && <span className="mono"> · {p.mountedDatabase.name}</span>}</span>
                                <span className="meta">renews {formatDate(p.expiresAt)}</span>
                                <StatusTag status={p.status} />
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
            {owned?.status === 'active' && !hasHosting && (
                <Link className="btn-outline small domain-group-action" to={`/panel/${owned.id}`}>+ Add web hosting</Link>
            )}
        </div>
    );
}

function Profile() {
    const { user, setUser } = useAuth();
    const [form, setForm] = useState({ name: user!.name, phone: user!.phone ?? '' });
    const [saved, setSaved] = useState(false);
    const { busy, error, run } = useAction();

    const submit = (e: FormEvent) => {
        e.preventDefault();
        setSaved(false);
        void run(async () => {
            const res = await api.patch<{ user: User }>('/auth/me', { name: form.name, phone: form.phone || null });
            setUser(res.data.user);
            setSaved(true);
        });
    };

    return (
        <div className="form-card">
            <h3>Profile</h3>
            {error && <Alert type="error">{error}</Alert>}
            {saved && <Alert type="success">Profile saved.</Alert>}
            <form className="fi-form" onSubmit={submit}>
                <label>Email<input type="email" value={user!.email} disabled /></label>
                <label>Name<input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /></label>
                <label>Mobile number<input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="070 123 45 67" /></label>
                <button className="btn-submit" disabled={busy}>{busy ? 'Saving…' : 'Save profile'}</button>
            </form>
        </div>
    );
}

function ChangePassword() {
    const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
    const [saved, setSaved] = useState(false);
    const { busy, error, setError, run } = useAction();

    const submit = (e: FormEvent) => {
        e.preventDefault();
        setSaved(false);
        if (form.newPassword !== form.confirm) return setError('Passwords do not match');
        void run(async () => {
            await api.post('/auth/password', { currentPassword: form.currentPassword, newPassword: form.newPassword });
            setForm({ currentPassword: '', newPassword: '', confirm: '' });
            setSaved(true);
        });
    };

    const update = (key: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [key]: e.target.value }));

    return (
        <div className="form-card">
            <h3>Change password</h3>
            {error && <Alert type="error">{error}</Alert>}
            {saved && <Alert type="success">Password changed.</Alert>}
            <form className="fi-form" onSubmit={submit}>
                <label>Current password<input type="password" value={form.currentPassword} onChange={update('currentPassword')} required autoComplete="current-password" /></label>
                <label>New password<input type="password" value={form.newPassword} onChange={update('newPassword')} required minLength={10} autoComplete="new-password" /></label>
                <label>Confirm new password<input type="password" value={form.confirm} onChange={update('confirm')} required minLength={10} autoComplete="new-password" /></label>
                <button className="btn-submit" disabled={busy}>{busy ? 'Saving…' : 'Change password'}</button>
            </form>
        </div>
    );
}

export default function Account() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const services = useLoad(() => api.get<{ services: Service[] }>('/services').then(r => r.data.services), []);
    const orders = useLoad(() => api.get<{ orders: Order[] }>('/orders').then(r => r.data.orders), []);

    const doLogout = async () => {
        await logout();
        navigate('/');
    };

    return (
        <>
            <section className="section page-section">
                <div className="panel-header">
                    <div>
                        <span className="section-label">My account</span>
                        <h1 className="page-title">Hi, {user!.name.split(' ')[0]}</h1>
                    </div>
                    <div className="btn-row">
                        <Link className="btn-submit inline" to="/hosting" style={{ textDecoration: 'none' }}>Buy hosting</Link>
                        <button className="btn-outline" onClick={doLogout}>Log out</button>
                    </div>
                </div>

                <h3 className="sub-title">Your services</h3>
                {services.loading && <Loading />}
                {services.error && <Alert type="error">{services.error}</Alert>}
                {services.data?.length === 0 && (
                    <div className="table-wrap"><div className="empty-state">
                        You have no services yet. <Link className="text-link" to="/hosting">Browse hosting plans →</Link>
                    </div></div>
                )}
                {!!services.data?.length && (
                    <div className="domain-groups">
                        {groupByDomain(services.data).map(g => <DomainGroupCard key={g.domain} group={g} />)}
                    </div>
                )}

                <h3 className="sub-title">Orders</h3>
                {orders.loading && <Loading />}
                {orders.error && <Alert type="error">{orders.error}</Alert>}
                {orders.data && (
                    <div className="table-wrap">
                        {orders.data.length === 0 ? <div className="empty-state">No orders yet.</div> : (
                            <table className="data-table">
                                <thead><tr><th>Order</th><th>Product</th><th>Domain</th><th>Date</th><th>Amount</th><th>Status</th><th /></tr></thead>
                                <tbody>
                                    {orders.data.map(o => (
                                        <tr key={o.id}>
                                            <td className="mono">{o.id.slice(0, 8).toUpperCase()}</td>
                                            <td>{o.productName}</td>
                                            <td>{o.domain}</td>
                                            <td>{formatDate(o.createdAt)}</td>
                                            <td>{formatPrice(o.amountOre)}</td>
                                            <td><StatusTag status={o.status} /></td>
                                            <td className="actions">
                                                {o.status === 'pending' && <Link className="btn-outline small" to={`/checkout/order/${o.id}`}>Pay</Link>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                <h3 className="sub-title">Settings</h3>
                <div className="card-grid cols-2">
                    <Profile />
                    <ChangePassword />
                </div>
            </section>
            <Footer />
        </>
    );
}
